import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { referralCode } from '../lib/referral';

/** A partner's offer (#598): who sent the family and the discount it gets at checkout. */
export interface PartnerOffer {
  code: string;
  name: string;
  discountPct: number;
  /** 0 = forever (a sign-up code, #879). */
  discountMonths: number;
  /** A sign-up code (#879) rather than a partner's: no name, maybe a Founder code. */
  kind?: 'code';
  founder?: boolean;
  /** Sign-ups are open for this code (always, or because the admin lets partners bring sign-ups). */
  signupsOpen: boolean;
}

/** The offer behind a code (the remembered one from ?partner= / ?ref= by default), or null when it isn't a partner's. */
export function usePartnerOffer(code = referralCode()): PartnerOffer | null {
  const [offer, setOffer] = useState<PartnerOffer | null>(null);
  useEffect(() => {
    const clean = code.trim();
    if (clean.length < 3) {
      setOffer(null);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      api
        .get<PartnerOffer>(`/api/partner-code/${encodeURIComponent(clean)}`)
        .then((o) => live && setOffer(o))
        .catch(() => live && setOffer(null));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [code]);
  return offer;
}

export const offerText = (o: Pick<PartnerOffer, 'discountPct' | 'discountMonths' | 'founder'>) =>
  o.founder
    ? `You’re a ClassGuardian Founder: ${o.discountPct}% off, for as long as you subscribe`
    : o.discountPct > 0 && o.discountMonths > 0
      ? `${o.discountPct}% off your first ${o.discountMonths === 1 ? 'month' : `${o.discountMonths} months`}`
      : o.discountPct > 0 && o.discountMonths === 0
        ? `${o.discountPct}% off, for as long as you subscribe`
        : '';

/** "Sunny Days Homeschool sent you 20% off your first 3 months", across the top of the home page and sign-up. */
export function PartnerBanner({ offer }: { offer: PartnerOffer | null }) {
  if (!offer || !offerText(offer)) return null;
  // A sign-up code (#879): no one sent it, so it says what it gives.
  if (offer.kind === 'code')
    return (
      <div className="partner-banner" role="status">
        🎁 Code {offer.code}: <strong>{offerText(offer)}</strong>. It’s applied when you subscribe.
      </div>
    );
  return (
    <div className="partner-banner" role="status">
      🎁 <strong>{offer.name}</strong> sent you <strong>{offerText(offer)}</strong>. It’s applied when you subscribe.
    </div>
  );
}
